import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth, authApi } from '@/services/api';

interface AuthGuardProps {
  children: ReactNode;
  allowedRoles?: string[];
  redirectTo?: string;
}

export default function AuthGuard({ children, allowedRoles, redirectTo = '/admin' }: AuthGuardProps) {
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!auth.isAuthenticated()) {
        if (!cancelled) setChecking(false);
        return;
      }

      try {
        const res = await authApi.me();
        if (!cancelled && res?.data) {
          const user = res.data as Record<string, unknown>;
          const role = (user.role as string) || auth.getRole() || '';
          auth.setRole(role);

          // If allowedRoles is specified, check against it
          if (allowedRoles && !allowedRoles.includes(role)) {
            if (!cancelled) setChecking(false);
            return;
          }

          if (!cancelled) setValid(true);
        }
      } catch {
        auth.logout();
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    verify();
    return () => { cancelled = true; };
  }, [allowedRoles]);

  if (checking) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          <p className="text-sm text-foreground-500">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    // Smart redirect based on role
    const currentRole = auth.getRole();
    const staffRoles = ['super_admin', 'admin', 'manager', 'agent'];
    const memberRoles = ['landowner', 'tenant', 'viewer'];

    if (currentRole && memberRoles.includes(currentRole)) {
      return <Navigate to="/members" state={{ from: location }} replace />;
    }
    if (currentRole && staffRoles.includes(currentRole)) {
      return <Navigate to="/admin" state={{ from: location }} replace />;
    }

    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}